import type {
  CalculatorData,
  CalculatorOption,
  LeasingRvOption
} from "../types/calculatorData";
import {
  getActiveLeasingSelectionFromTree,
  getActiveRentSelectionFromTree
} from "./getActiveCalculatorSelection";

/** Filter keys used when building CalculationResult from selected calculator options. */
export interface CalculatorPriceFilters {
  Period: number;
  Ow: number;
  Mileage?: number;
  Rv?: number;
}

export interface SelectedCalculatorPrices {
  /** Monthly installment — rent: selected mileage `Price`, leasing: selected RV `Price`. */
  installmentPrice: number | undefined;
  /** Up-front payment (OW) for the selected combination. */
  owPrice: number | undefined;
  /** Leasing buyout installment component (`Rv.Price`). Same as installmentPrice for leasing. */
  rvPrice: number | undefined;
  /** Leasing residual value in PLN (`Rv.ValuePLN`). */
  rvValuePLN: number | undefined;
  /** Insurance addon for the selected period. */
  insurancePrice: number | undefined;
  period: number | undefined;
}

function getInsuranceForPeriod(calculatorData: CalculatorData, periodValue: number): number | undefined {
  return calculatorData.Insurance.find((item) => item.Period === periodValue)?.Value;
}

function buildResult(
  period: CalculatorOption,
  ow: CalculatorOption,
  installmentOption: CalculatorOption | LeasingRvOption | undefined,
  calculatorData: CalculatorData,
  rvValuePLN?: number
): SelectedCalculatorPrices {
  return {
    installmentPrice: installmentOption?.Price,
    owPrice: ow.Price,
    rvPrice: installmentOption?.Price,
    rvValuePLN,
    insurancePrice: getInsuranceForPeriod(calculatorData, period.Value),
    period: period.Value
  };
}

export function getRentPricesFromFilters(
  calculatorData: CalculatorData,
  filters: Pick<CalculatorPriceFilters, "Period" | "Ow" | "Mileage">
): SelectedCalculatorPrices | null {
  const periodGroup = calculatorData.RentPeriods.find((item) => item.Period.Value === filters.Period);
  const owGroup = periodGroup?.Ows.find((item) => item.Ow.Value === filters.Ow);
  const mileage = owGroup?.Mileages.find((item) => item.Value === filters.Mileage);

  if (!periodGroup || !owGroup || !mileage) {
    return null;
  }

  return buildResult(periodGroup.Period, owGroup.Ow, mileage, calculatorData);
}

export function getLeasingPricesFromFilters(
  calculatorData: CalculatorData,
  filters: Pick<CalculatorPriceFilters, "Period" | "Ow" | "Rv">
): SelectedCalculatorPrices | null {
  const periodGroup = calculatorData.LeasingPeriods.find((item) => item.Period.Value === filters.Period);
  const owGroup = periodGroup?.Ows.find((item) => item.Ow.Value === filters.Ow);
  const rv = owGroup?.Rvs.find((item) => item.Value === filters.Rv);

  if (!periodGroup || !owGroup || !rv) {
    return null;
  }

  return buildResult(periodGroup.Period, owGroup.Ow, rv, calculatorData, rv.ValuePLN);
}

/** Reads selections from raw calculator tree (`Status: true` flags). */
export function getSelectedPricesFromCalculatorData(
  calculatorData: CalculatorData,
  financingMethod: number
): SelectedCalculatorPrices | null {
  if (financingMethod === 1) {
    const selection = getActiveRentSelectionFromTree(calculatorData.RentPeriods);
    if (!selection) {
      return null;
    }
    return buildResult(
      selection.period,
      selection.ow,
      selection.mileage,
      calculatorData
    );
  }

  if (financingMethod === 2) {
    const selection = getActiveLeasingSelectionFromTree(calculatorData.LeasingPeriods);
    if (!selection) {
      return null;
    }
    return buildResult(
      selection.period,
      selection.ow,
      selection.rv,
      calculatorData,
      selection.rv.ValuePLN
    );
  }

  return null;
}
